/**
 * What the Help page says, and who decides it.
 *
 * THE FIRST VERSION WAS WRITTEN INTO THE PAGE and was wrong within a day. It explained approval
 * chains, approvers acting from an email, matters stuck with somebody who had left — none of
 * which happens here, because `settings/general.workflowEnabled` is off and submitting a matter
 * produces a PDF. Prose about how the app behaves is configuration, and configuration does not
 * belong in a component.
 *
 * So there are two layers:
 *
 *   1. DEFAULTS, computed from the settings that actually decide the behaviour. Out of the box the
 *      page is correct whichever way the workflow switch is set, and it names the real submit
 *      button rather than a guess at it.
 *   2. AN ADMIN'S OWN TEXT, stored in `settings/helpPage`, which wins wherever it is present.
 *      Editing a section takes ownership of that section and nothing else; a section left alone
 *      keeps following the defaults, so turning the workflow on later fixes the untouched parts
 *      by itself.
 *
 * "What each page is for" is deliberately NOT text. It is generated from the live form layout
 * (see walkthroughSteps.js), because that list has already been wrong once for exactly the reason
 * this file exists.
 */

/** A stable id per section, so an admin's edit survives the default text being rewritten. */
export const SECTION_IDS = {
  OPENING: 'opening',
  TEMPLATES: 'templates',
  REQUIREMENTS: 'requirements',
  AFTER: 'after',
};

/**
 * The built-in text, for the firm as it is configured right now.
 *
 * @param {object} args
 * @param {boolean} args.workflowEnabled  whether this person's submissions route for approval
 * @param {string}  args.submitLabel      the real label on the submit button
 */
export function defaultHelpContent({ workflowEnabled, submitLabel } = {}) {
  const button = String(submitLabel || '').trim() || (workflowEnabled ? 'Submit for approval' : 'Send / Download PDF');

  return {
    title: 'How this works',
    intro: workflowEnabled
      ? 'Opening a matter, what happens to it afterwards, and the questions people ask most.'
      : 'Opening a matter, getting the completed PDF, and the questions people ask most.',

    sections: [
      {
        id: SECTION_IDS.OPENING,
        title: 'Opening a matter',
        body: [
          'Start at **New matter** in the left rail. A draft is created as soon as you begin, and it saves as you type — there is no Save button to forget, and closing the tab loses nothing. Everything you have started is under **My matters**.',
          'The form is one page per topic. You can move between them freely and answer them in any order; nothing is checked until you submit.',
        ],
      },
      {
        id: SECTION_IDS.TEMPLATES,
        title: 'Templates, for the client you open matters for often',
        body: [
          'If you open several matters for the same client, most of the form is the same every time. Fill it in once, then use **Save as template** at the bottom of the form and give it a name. **Templates** in the left rail lists them, and **Start a matter** opens a new form already filled in.',
          '**Only you can see your templates.** A template is a copy of a real matter, carrying a real client’s details, so it is not shared with anybody else.',
          'A template never carries matter numbers, attachments, or any field an administrator has marked as never-copy — so there is no risk of last month’s paperwork following you into this month’s matter.',
        ],
      },
      {
        id: SECTION_IDS.REQUIREMENTS,
        title: 'Required, and "usually provided"',
        body: [
          'A **required** answer blocks submission until it is given.',
          'A **usually provided** answer can be left blank, but you will be asked to say briefly why. That reason travels with the matter and is kept with the record. It is not a way around the question — it is how you answer honestly when there is genuinely nothing to give, so that the truthful answer is never the one that cannot be submitted.',
          'When something is still missing, the form says so before you press anything and takes you straight to it.',
        ],
      },
      workflowEnabled
        ? {
            id: SECTION_IDS.AFTER,
            title: 'What happens after you submit',
            body: [
              `**${button}** stops the matter being yours to edit and starts it moving. The approvals it needs are worked out from your answers and from your practice group's own routing, and the first approver is emailed straight away.`,
              'They can approve from the email itself without signing in, which is why approvals usually come back the same day. Open the matter from **My matters** to see who has approved, who it is sitting with, and everything anybody has said about it.',
              'If an approver asks for more information it comes back to you with their question attached. You answer it and it carries on from where it stopped rather than starting again.',
            ],
          }
        : {
            id: SECTION_IDS.AFTER,
            title: 'What happens when you finish',
            body: [
              `You are asked to check your answers, and then **${button}** completes the matter and produces the PDF — the full record of everything you entered.`,
              'It is emailed to you with the PDF attached, and stays available from the matter under **My matters**, so you can download it again whenever you need it.',
              'Nothing is routed anywhere and nobody is asked to approve it. What you do with the PDF afterwards is up to you.',
            ],
          },
    ],

    questions: [
      {
        id: 'mistake',
        q: 'I made a mistake after finishing.',
        a: [
          'Open the matter from **My matters** and tell an administrator what needs to change. Submitted answers are deliberately not editable in place — the PDF is a record of what was entered, and a record that can be quietly altered afterwards is not a record.',
        ],
      },
      {
        id: 'pdf-again',
        q: 'I have lost the PDF.',
        a: [
          'Open the matter from **My matters** and download it again. It is kept with the matter; the emailed copy is a convenience, not the only one.',
        ],
      },
      {
        id: 'later',
        q: 'Can I see what I entered months later?',
        a: [
          'Yes. The matter keeps every answer, and the PDF is kept alongside it for as long as the matter exists.',
        ],
      },
      {
        id: 'same-client-again',
        q: 'I open matters for the same client all the time. Do I have to retype everything?',
        a: [
          'No. Fill one in, use **Save as template** at the bottom of the form, and start from it next time — **Templates** in the left rail. Only you can see your own.',
        ],
      },
      {
        id: 'draft-lost',
        q: 'I closed the tab before I finished.',
        a: [
          'Nothing is lost. Drafts save as you type — reopen it from **My matters** and carry on from where you stopped.',
        ],
      },
      ...(workflowEnabled
        ? [
            {
              id: 'not-moved',
              q: 'My matter has not moved. What do I do?',
              a: [
                'Open it from **My matters** and look at who it is sitting with. You can send that person a reminder from the matter itself.',
                'If they are away, an administrator can reassign the step to somebody else.',
              ],
            },
            {
              id: 'why-approval',
              q: "Why did my matter need an approval somebody else's did not?",
              a: [
                'Approvals are worked out from your answers and from your practice group. Work introduced by a firm employee, a client who had other counsel on the matter, and several other answers each add a step.',
              ],
            },
            {
              id: 'left-firm',
              q: 'Someone has left the firm and a matter is stuck with them.',
              a: [
                'An administrator can reassign the step. The matter is never skipped past an approver — it waits, deliberately, until somebody is named in their place.',
              ],
            },
          ]
        : []),
    ],
  };
}

/**
 * An admin's stored text laid over the defaults.
 *
 * Per FIELD, not per document: a stored title with no stored body keeps the default body. That is
 * what lets somebody rename a section without inheriting responsibility for keeping its text
 * accurate, and it is why an untouched section still corrects itself when the workflow switch
 * changes.
 *
 * Sections and questions are matched by `id`. A stored entry whose id is not in the defaults is an
 * addition and is kept; one marked `isActive: false` is dropped. Order follows the stored list
 * where there is one, so an admin can reorder without retyping anything.
 */
export function mergeHelpContent(stored, defaults) {
  const base = defaults || defaultHelpContent({});
  // No stored document is the same as an empty one, rather than a separate path — a second return
  // here is how the page guide flag went missing from the commonest case of all.
  const saved = stored && typeof stored === 'object' ? stored : {};

  const overlay = (defaultList, storedList, fields) => {
    const byId = new Map((defaultList || []).map((entry) => [entry.id, entry]));
    if (!Array.isArray(storedList) || !storedList.length) return defaultList || [];

    const seen = new Set();
    const merged = [];
    for (const item of storedList) {
      if (!item || typeof item !== 'object' || !item.id) continue;
      seen.add(item.id);
      if (item.isActive === false) continue;
      const fallback = byId.get(item.id) || {};
      const entry = { id: item.id };
      for (const field of fields) {
        const value = item[field];
        const filled = Array.isArray(value) ? value.filter((line) => String(line).trim()) : String(value || '').trim();
        entry[field] = filled && filled.length ? value : fallback[field];
      }
      // An addition with nothing in it is not worth a heading.
      if (fields.some((field) => entry[field] && entry[field].length)) merged.push(entry);
    }
    // Defaults the admin has never touched still appear, after anything they arranged, so a new
    // built-in section is not invisible until somebody notices it.
    for (const entry of defaultList || []) if (!seen.has(entry.id)) merged.push(entry);
    return merged;
  };

  return {
    title: String(saved.title || '').trim() || base.title,
    intro: String(saved.intro || '').trim() || base.intro,
    sections: overlay(base.sections, saved.sections, ['title', 'body']),
    questions: overlay(base.questions, saved.questions, ['q', 'a']),
    // Off hides the generated "What each page is for" list. It is generated rather than typed, so
    // the only thing an admin can sensibly decide about it is whether to show it.
    showPageGuide: saved.showPageGuide !== false,
  };
}
